import { Table, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useTableScrollAndResize } from "@/hooks/useTableScrollAndResize";
import { cn } from "@/lib/utils";
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { forwardRef, PropsWithChildren, useMemo } from "react";
import usePeriodsColumns from "../hooks/usePeriodsColumns";
import PeriodFilter from "./PeriodFilter";
import PeriodsFooter from "./PeriodsFooter";
import PeriodsTableBody from "./PeriodsTableBody";
import { useProjectStoreContext } from "./ProjectStoreContext";

interface PeriodTableData {
  id: string;
  [periodId: string]: string | boolean;
}

export interface PeriodsTableProps extends PropsWithChildren {
  defaultWidth?: number;
  onScroll?: (scrollLeft: number, scrollTop: number) => void;
  onRowMouseEnter?: (rowId: string) => void;
  onRowMouseLeave?: (rowId: string) => void;
}

export const PeriodsTable = forwardRef<HTMLDivElement, PeriodsTableProps>(
  ({ onScroll, onRowMouseEnter, onRowMouseLeave }, ref) => {
    const { data, periodFilter } = useProjectStoreContext();
    const { columns } = usePeriodsColumns<PeriodTableData>(data, periodFilter);

    const tableData = useMemo(() => {
      return data.map((store) => ({
        id: store.id,
        ...Object.fromEntries(store.periods.map((period) => [period.id, true])),
      }));
    }, [data]);

    const table = useReactTable<PeriodTableData>({
      data: tableData,
      getRowId: (row) => row.id,
      columns,
      getCoreRowModel: getCoreRowModel(),
      state: {
        columnPinning: {
          left: ["selectStore"],
        },
      },
      defaultColumn: {
        size: 50,
        minSize: 50,
        maxSize: 50,
      },
    });

    const headers = table.getFlatHeaders();
    const precedingPinnedColumns = table.getLeftLeafHeaders();

    const columnsStyle = useMemo(() => {
      const style: Record<string, number> = {};
      for (const header of headers) {
        style[`--header-${header.id}-size`] = header.getSize();
        style[`--col-${header.column.id}-size`] = header.column.getSize();
        style[`--col-${header.column.id}-position`] = precedingPinnedColumns
          .filter(
            (c) =>
              c.column.getIsPinned() &&
              c.column.getPinnedIndex() < header.column.getPinnedIndex(),
          )
          .reduce((acc, c) => acc + c.getSize(), 0);
      }

      return style;
    }, [headers, precedingPinnedColumns]);

    useTableScrollAndResize<PeriodTableData>({
      tableContainerRef: ref,
      onScroll,
      table,
    });

    const totalSize = table.getTotalSize();
    const tableStyle = useMemo(() => {
      return {
        ...columnsStyle,
        minWidth: totalSize + "px",
      };
    }, [columnsStyle, totalSize]);

    return (
      <section className="whitespace-nowrap relative">
        <div ref={ref} className="overflow-x-auto overflow-y-visible w-full">
          <Table
            className="grid table-fixed auto-rows-min overflow-visible"
            style={tableStyle}
            data-testid="periods-table"
          >
            <colgroup>
              {table
                .getHeaderGroups()
                .map((headerGroup) =>
                  headerGroup.headers.map((header) => (
                    <col key={header.id} width="20px" />
                  )),
                )}
            </colgroup>

            <TableHeader className="sticky top-0 z-30 backdrop-blur-md bg-slate-200/70">
              <TableRow>
                <TableHead
                  colSpan={table.getAllColumns().length}
                  className="h-12 sticky left-0"
                  style={{
                    width: `calc(var(--periods-table-width) * 1px)`,
                  }}
                >
                  <PeriodFilter filter={periodFilter} />
                </TableHead>
              </TableRow>
            </TableHeader>
            {table.getHeaderGroups().map((headerGroup, index) => (
              <TableHeader
                key={headerGroup.id}
                className={cn("sticky z-30 backdrop-blur-md bg-slate-100/70", {
                  "top-12": index === 0,
                  "top-24": index === 1,
                })}
              >
                <TableRow>
                  {headerGroup.headers.map((header) => (
                    <TableHead
                      key={header.id}
                      className={cn("h-12 !p-0 text-center", {
                        " bg-slate-200 sticky left-0":
                          header.column.getIsPinned() === "left",
                      })}
                      style={{
                        width: `calc(var(--header-${header.id}-size) * 1px)`,
                      }}
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
            ))}
            <PeriodsTableBody
              table={table}
              onRowMouseEnter={onRowMouseEnter}
              onRowMouseLeave={onRowMouseLeave}
            />
          </Table>
        </div>
        <div className="bg-slate-200/70 sticky bottom-1 backdrop-blur-md">
          <div className="hover:bg-transparent sticky left-0 px-1 py-3 flex items-center">
            <PeriodsFooter />
          </div>
        </div>
      </section>
    );
  },
);

PeriodsTable.displayName = "PeriodsTable";
